import { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DatePicker,
  ModalDefault,
  NoResults,
  Notice,
  PageActions,
  SelectionBar,
  SelectorPageStatus,
  Skeleton,
  StatePill,
  Text,
  Tooltip,
  Totalizers,
  createSelectionColumn,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import HelpVideoButton from "@components/HelpVideoButton";
import RejectHoursModal from "@components/RejectHoursModal";
import { hasPermission } from "@helpers/permissions";
import { DomainStatus, escapeHtml, formatBRL, formatCompetencia, formatDate, formatHours, usePageHeader } from "@components/ow";

const FIXED_MODE = "FIXED_WITH_HOUR_ADJUSTMENT";

const FIXED_MODE_HINT =
  "Contrato de valor fixo: as horas ajustam o pagamento do mês, não o definem. " +
  "O valor sai no cronograma de pagamento.";

const ABSENCE_HINT = "Ausência não gera valor.";

const NO_VALUES_HINT = "Você não tem permissão para ver os valores deste projeto.";
const NO_VALUES_NOTE = "não inclui projetos sem permissão de ver valores";

const HOUR_STATUS = { WAITING: "aguardando", APPROVED: "aprovado", REJECTED: "reprovado", EMITED: "nota_emitida" };

// Pílulas do filtro de status: uma por vez, "Todos" sem filtro
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Aguardando", value: "WAITING" },
  { label: "Aprovado", value: "APPROVED" },
  { label: "Reprovado", value: "REJECTED" },
  { label: "Nota emitida", value: "EMITED" },
];

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
`;

const CompetenceField = styled.div`
  width: 220px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "16px"};
`;

const Inline = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

// Aprovar e Reprovar sempre lado a lado, mesmo com a coluna apertada
const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

const Help = styled.span`
  cursor: help;
`;

const ProjectSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

/** Horas decimais da API (7.5) no padrão do design system ("7h30"); nunca decimal. */
const hrs = (value) => formatHours((parseFloat(value) || 0) * 60);

/** "Ver valores das horas" no projeto: sem ela, o back não manda valor/hora nem R$. */
function canSeeValues(projectId) {
  return hasPermission("project_hours:read_values", projectId);
}

/** Projetos com horas aguardando. */
function waitingProjects(contractor) {
  return (contractor?.projects || []).filter((p) => p.hours_waiting > 0);
}

/** Projetos com horas aguardando cujo valor o usuário não vê (fora de qualquer R$). */
function hiddenValueProjectsWaiting(contractor) {
  return waitingProjects(contractor).filter((p) => !canSeeValues(p.id));
}

/** Nada do que está aguardando tem valor visível: não há R$ a mostrar. */
function noVisibleValueWaiting(contractor) {
  const waiting = waitingProjects(contractor);
  return waiting.length > 0 && waiting.every((p) => !canSeeValues(p.id));
}

/**
 * Projetos de contrato de valor fixo com horas aguardando.
 *
 * A decisão é por projeto, não por prestador: quem tem contrato fixo num projeto
 * pode ter contrato por hora em outro, e aí metade do valor da linha é real.
 */
function fixedProjectsWaiting(contractor) {
  return waitingProjects(contractor).filter((p) => p.hour_tracking_mode === FIXED_MODE && canSeeValues(p.id));
}

/** Tudo o que está aguardando (com valor visível) vem de contrato de valor fixo: não há R$ a somar. */
function onlyFixedWaiting(contractor) {
  const waiting = waitingProjects(contractor).filter((p) => canSeeValues(p.id));
  return waiting.length > 0 && waiting.every((p) => p.hour_tracking_mode === FIXED_MODE);
}

/**
 * Quem vê as horas pode não poder decidir sobre todas: a lista vem pelo escopo de
 * "ver horas" e a aprovação vale por projeto. Aprovar e reprovar andam juntos no
 * catálogo, então as ações pedem as duas no projeto da hora.
 */
function canDecide(projectId) {
  return hasPermission("project_hours:approve", projectId) && hasPermission("project_hours:reject", projectId);
}

/** Horas aguardando do prestador sobre as quais o usuário pode decidir. */
function actionableHours(contractor) {
  return (contractor?.hour_records || []).filter((h) => h.status === "WAITING" && canDecide(h.project_id));
}

const actionableHourIds = (contractor) => actionableHours(contractor).map((h) => h.id);

const hasWaitingHours = (contractor) => (contractor.waiting_hour_ids || []).length > 0;

/** Horas, valor e prestadores do que vai ser decidido (só o que está no escopo de aprovação). */
function summaryOf(list) {
  let hours = 0;
  let value = 0;
  let count = 0;
  let fixedCount = 0;
  let hiddenCount = 0;
  for (const c of list) {
    const actionable = actionableHours(c);
    if (actionable.length === 0) continue;
    count++;
    if (actionable.length === (c.waiting_hour_ids || []).length) {
      hours += c.total_hours_waiting || 0;
      value += c.total_value_waiting || 0;
      if (fixedProjectsWaiting(c).length > 0) fixedCount++;
      if (hiddenValueProjectsWaiting(c).length > 0) hiddenCount++;
    } else {
      // Só parte das horas é do escopo de aprovação: soma só essas
      for (const h of actionable) {
        hours += h.total_hours || 0;
        if (canSeeValues(h.project_id) && h.hour_tracking_mode !== FIXED_MODE && h.record_type !== "ABSENCE") {
          value += (h.total_hours || 0) * (h.hourly_rate || 0);
        }
      }
      if (actionable.some((h) => canSeeValues(h.project_id) && h.hour_tracking_mode === FIXED_MODE)) fixedCount++;
      if (actionable.some((h) => !canSeeValues(h.project_id))) hiddenCount++;
    }
  }
  return { hours, value, count, fixedCount, hiddenCount };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Traço com explicação no hover, onde mostrar um R$ seria mentir. */
function HintDash({ hint }) {
  return (
    <Tooltip content={hint} $position="top" $size="medium" $color="dark">
      <Help>
        <Text $text="—" $variant="paragraph_small_medium" $color="N400" />
      </Help>
    </Tooltip>
  );
}

/** Para os botões da linha não abrirem os detalhes (a lib só ignora links e as colunas de ação). */
const stop = (fn) => (e) => {
  e?.stopPropagation?.();
  fn();
};

export default function HoursApproval() {
  const projectHours = useService("projectHours");
  // Colunas e totais em R$ só para quem vê valores em algum projeto (ADM sempre)
  const seesAnyValues = hasPermission("project_hours:read_values");

  usePageHeader({
    title: "Aprovação de horas",
    breadcrumb: [{ page: "Aprovação de horas", route: "/c/hours-approval" }],
  });

  const [loading, setLoading] = useState(true);
  const [contractors, setContractors] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [loadingAction, setLoadingAction] = useState(false);

  // Filtros
  const currentDate = new Date();
  const [competence, setCompetence] = useState({ month: currentDate.getMonth() + 1, year: currentDate.getFullYear() });
  const [filterStatus, setFilterStatus] = useState(STATUS_DATA[0]);
  const [autoFilterApplied, setAutoFilterApplied] = useState(false);

  // Aprovação em lote, com confirmação: { ids, summary }
  const [approveBatch, setApproveBatch] = useState(null);
  // Reprovação com motivo (linha, lote ou lançamento): { ids, title, summary }
  const [rejectTarget, setRejectTarget] = useState(null);

  // Detalhes do prestador
  const [detailsUserId, setDetailsUserId] = useState(null);
  const [loadingDetailAction, setLoadingDetailAction] = useState(null);

  const reference = `${competence.year}-${String(competence.month).padStart(2, "0")}`;
  const competenceLabel = formatCompetencia(competence, true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectHours.consolidated(reference);
      const data = result?.data || result || [];
      const list = Array.isArray(data) ? data : [];
      setContractors(list);

      // Abre em "Aguardando" quando há horas aguardando na competência
      if (!autoFilterApplied) {
        if (list.some(hasWaitingHours)) {
          setFilterStatus(STATUS_DATA.find((s) => s.value === "WAITING"));
        }
        setAutoFilterApplied(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar dados consolidados");
    }
    setLoading(false);
    setRowSelection({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Trocar o filtro limpa a seleção, como antes
  useEffect(() => {
    setRowSelection({});
  }, [filterStatus]);

  const filteredContractors = contractors.filter((c) => {
    switch (filterStatus.value) {
      case "WAITING":
        return hasWaitingHours(c);
      case "APPROVED":
        return c.total_hours_approved > 0;
      case "REJECTED":
        return c.total_hours_rejected > 0;
      case "EMITED":
        return c.total_hours_emited > 0;
      default:
        return true;
    }
  });

  const contractorsById = new Map(contractors.map((c) => [String(c.user_id), c]));
  const selectedContractors = Object.keys(rowSelection)
    .filter((id) => rowSelection[id])
    .map((id) => contractorsById.get(id))
    .filter(Boolean);
  const selectedSummary = summaryOf(selectedContractors);
  const detailsContractor = detailsUserId ? contractorsById.get(String(detailsUserId)) : null;

  // Prestador que sumiu da lista depois de uma ação fecha os detalhes
  useEffect(() => {
    if (detailsUserId && !loading && !contractorsById.has(String(detailsUserId))) setDetailsUserId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractors, loading]);

  // Totais
  const totalWaitingHours = filteredContractors.reduce((s, c) => s + (c.total_hours_waiting || 0), 0);
  const totalWaitingValue = filteredContractors.reduce((s, c) => s + (c.total_value_waiting || 0), 0);
  const totalApprovedHours = filteredContractors.reduce((s, c) => s + (c.total_hours_approved || 0), 0);
  const hasFixedWaiting = filteredContractors.some((c) => fixedProjectsWaiting(c).length > 0);
  const hasHiddenValueWaiting = filteredContractors.some((c) => hiddenValueProjectsWaiting(c).length > 0);

  const totals = [{ name: "Horas aguardando", value: hrs(totalWaitingHours), color: "N900" }];
  if (seesAnyValues) totals.push({ name: "Valor aguardando", value: formatBRL(totalWaitingValue), color: "N900" });
  totals.push({ name: "Horas aprovadas", value: hrs(totalApprovedHours), color: "N900" });

  const valueNotes = [
    hasFixedWaiting ? "não inclui contratos de valor fixo" : null,
    hasHiddenValueWaiting ? NO_VALUES_NOTE : null,
  ].filter(Boolean);

  // === Ações ===
  async function decide(ids, approve, rejectionReason) {
    await projectHours.approve({ hours: ids, approve, ...(!approve && { rejection_reason: rejectionReason }) });
  }

  async function approveContractor(contractor) {
    const ids = actionableHourIds(contractor);
    if (!ids.length) return;
    setLoadingAction(true);
    try {
      await decide(ids, true);
      toast.success("Horas aprovadas!");
      fetchData();
    } catch (err) {
      toast.error("Erro ao processar");
    }
    setLoadingAction(false);
  }

  function openApproveBatch() {
    const ids = selectedContractors.flatMap(actionableHourIds);
    if (!ids.length) {
      toast.warn("Nenhuma hora aguardando aprovação nos prestadores selecionados");
      return;
    }
    setApproveBatch({ ids, summary: selectedSummary });
  }

  async function confirmApproveBatch() {
    setLoadingAction(true);
    try {
      await decide(approveBatch.ids, true);
      toast.success("Horas aprovadas com sucesso!");
      setApproveBatch(null);
      fetchData();
    } catch (err) {
      toast.error("Erro ao processar aprovação");
    }
    setLoadingAction(false);
  }

  function openRejectContractors(list) {
    const ids = list.flatMap(actionableHourIds);
    if (!ids.length) {
      toast.warn("Nenhuma hora aguardando aprovação nos prestadores selecionados");
      return;
    }
    const summary = summaryOf(list);
    const single = list.length === 1 ? list[0] : null;
    setRejectTarget({
      ids,
      batch: !single,
      title: single ? `Reprovar horas de ${single.user?.name || "prestador"}` : `Reprovar horas de ${summary.count} prestadores`,
      summary: {
        text: single ? "<strong>%s</strong> aguardando em %s. %s" : "<strong>%s</strong> de <strong>%s</strong> em %s. %s",
        replaces: single
          ? [hrs(summary.hours), competenceLabel, "As horas voltam para o prestador como reprovadas."]
          : [hrs(summary.hours), plural(summary.count, "prestador", "prestadores"), competenceLabel, "As horas voltam para os prestadores como reprovadas."],
      },
    });
  }

  function openRejectHour(hour) {
    setRejectTarget({
      ids: [hour.id],
      hourId: hour.id,
      title: "Reprovar lançamento",
      summary: {
        text: "<strong>%s</strong> em %s · %s. O lançamento volta para o prestador como reprovado.",
        replaces: [
          hour.record_type === "ABSENCE" ? plural(parseFloat(hour.total_hours) || 0, "dia", "dias") : hrs(hour.total_hours),
          formatDate(hour.reference_date),
          escapeHtml(hour.project_name || "—"),
        ],
      },
    });
  }

  async function confirmReject(reason) {
    const target = rejectTarget;
    setLoadingAction(true);
    if (target.hourId) setLoadingDetailAction(target.hourId);
    try {
      await decide(target.ids, false, reason);
      toast.success(target.hourId ? "Hora reprovada!" : target.batch ? "Horas reprovadas com sucesso!" : "Horas reprovadas!");
      setRejectTarget(null);
      await fetchData();
    } catch (err) {
      toast.error(target.batch ? "Erro ao processar aprovação" : "Erro ao processar");
    }
    setLoadingAction(false);
    setLoadingDetailAction(null);
  }

  async function approveHour(hourId) {
    setLoadingDetailAction(hourId);
    try {
      await decide([hourId], true);
      toast.success("Hora aprovada!");
      await fetchData();
    } catch (err) {
      toast.error("Erro ao processar");
    }
    setLoadingDetailAction(null);
  }

  // === Células ===
  function projectTooltip(projects) {
    return (
      <ProjectSummary>
        {projects.map((p) => {
          const isFixed = p.hour_tracking_mode === FIXED_MODE;
          const showValues = canSeeValues(p.id);
          const parts = [];
          if (showValues) {
            parts.push(isFixed ? `Valor fixo · ajuste de ${formatBRL(p.hourly_rate)}/h` : `Taxa: ${formatBRL(p.hourly_rate)}/h`);
          } else if (isFixed) {
            parts.push("Valor fixo");
          }
          if (p.hours_waiting > 0) {
            parts.push(`Aguardando: ${hrs(p.hours_waiting)}` + (showValues && !isFixed ? ` (${formatBRL(p.value_waiting)})` : ""));
          }
          if (p.hours_approved > 0) parts.push(`Aprovadas: ${hrs(p.hours_approved)}`);
          return (
            <div key={p.id}>
              <Text $text={escapeHtml(p.name)} $variant="caption_bold" $color="N0" />
              <Text $text={escapeHtml(parts.join(" · "))} $variant="caption_medium" $color="N100" />
            </div>
          );
        })}
      </ProjectSummary>
    );
  }

  function waitingValueCell(c) {
    if (!hasWaitingHours(c)) return <Text $text={formatBRL(0)} $variant="paragraph_small_medium" $color="N400" />;
    if (noVisibleValueWaiting(c)) return <HintDash hint={NO_VALUES_HINT} />;
    if (onlyFixedWaiting(c) && hiddenValueProjectsWaiting(c).length === 0) return <HintDash hint={FIXED_MODE_HINT} />;
    const fixed = fixedProjectsWaiting(c);
    const hidden = hiddenValueProjectsWaiting(c);
    const value = <Text $text={formatBRL(c.total_value_waiting || 0)} $variant="paragraph_small_bold" $color="N900" />;
    if (!fixed.length && !hidden.length) return value;
    // Parte do que está aguardando não entra no R$: o "*" explica o quê
    const hint = [
      fixed.length ? `Não inclui ${fixed.map((p) => p.name).join(", ")}: ${FIXED_MODE_HINT}` : null,
      hidden.length ? `Não inclui ${hidden.map((p) => p.name).join(", ")}: ${NO_VALUES_HINT}` : null,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <Tooltip content={hint} $position="top" $size="large" $color="dark">
        <Help>
          <Text $text={`${formatBRL(c.total_value_waiting || 0)} *`} $variant="paragraph_small_bold" $color="N900" />
        </Help>
      </Tooltip>
    );
  }

  function statusCell(c) {
    if (hasWaitingHours(c)) return <DomainStatus domain="horas" value="aguardando" />;
    return (
      <Inline>
        {c.total_hours_approved > 0 ? <DomainStatus domain="horas" value="aprovado" /> : null}
        {c.total_hours_rejected > 0 ? <DomainStatus domain="horas" value="reprovado" /> : null}
        {c.total_hours_emited > 0 ? <DomainStatus domain="horas" value="nota_emitida" /> : null}
      </Inline>
    );
  }

  // Sem nada que a pessoa possa decidir na lista (só leitura, ou nada aguardando), a seleção sai
  const anySelectable = filteredContractors.some((c) => actionableHourIds(c).length > 0);

  const columns = [
    ...(anySelectable ? [createSelectionColumn()] : []),
    {
      id: "contractor",
      header: "Prestador",
      size: 240,
      maxSize: 280,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.user?.name || "—")} $variant="paragraph_small_bold" $color="N900" />
          {row.original.user?.email ? (
            <Text $text={escapeHtml(row.original.user.email)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    {
      id: "projects",
      header: "Projetos",
      size: 220,
      maxSize: 300,
      cell: ({ row }) => {
        const projects = row.original.projects || [];
        if (!projects.length) return "—";
        return (
          <Tooltip content={projectTooltip(projects)} $position="top" $size="large" $color="dark">
            <Help>
              <Text $text={escapeHtml(projects.map((p) => p.name).join(", "))} $variant="paragraph_small_medium" $color="N900" />
            </Help>
          </Tooltip>
        );
      },
    },
    {
      id: "waitingHours",
      header: "Horas aguardando",
      size: 130,
      align: "right",
      cell: ({ row }) => (
        <Text
          $text={hrs(row.original.total_hours_waiting)}
          $variant={hasWaitingHours(row.original) ? "paragraph_small_bold" : "paragraph_small_medium"}
          $color={hasWaitingHours(row.original) ? "N900" : "N400"}
        />
      ),
    },
    ...(seesAnyValues
      ? [{ id: "waitingValue", header: "Valor aguardando", size: 150, align: "right", cell: ({ row }) => waitingValueCell(row.original) }]
      : []),
    { id: "status", header: "Status", size: 150, cell: ({ row }) => statusCell(row.original) },
    {
      id: "actions",
      size: 200,
      maxSize: 200,
      align: "right",
      cell: ({ row }) =>
        actionableHourIds(row.original).length > 0 ? (
          <Actions>
            <Button
              $text="Aprovar"
              $variant="outline"
              $size="small"
              $noWidth
              $isDisabled={loadingAction}
              $action={stop(() => approveContractor(row.original))}
            />
            <Button
              $text="Reprovar"
              $variant="outline_danger"
              $size="small"
              $noWidth
              $isDisabled={loadingAction}
              $action={stop(() => openRejectContractors([row.original]))}
            />
          </Actions>
        ) : null,
    },
  ];

  const detailColumns = [
    { id: "date", header: "Data", size: 95, cell: ({ row }) => formatDate(row.original.reference_date) },
    { id: "project", header: "Projeto", size: 130, cell: ({ row }) => row.original.project_name || "—" },
    {
      id: "type",
      header: "Tipo",
      size: 90,
      cell: ({ row }) =>
        row.original.record_type === "ABSENCE" ? (
          <StatePill $text="Ausência" $variant="orange" $size="small" />
        ) : (
          <StatePill $text="Horas" $variant="gray" $size="small" />
        ),
    },
    {
      id: "hours",
      header: "Horas",
      size: 70,
      align: "right",
      cell: ({ row }) =>
        row.original.record_type === "ABSENCE"
          ? plural(parseFloat(row.original.total_hours) || 0, "dia", "dias")
          : hrs(row.original.total_hours),
    },
    ...(seesAnyValues
      ? [
          {
            id: "value",
            header: "Valor",
            size: 100,
            align: "right",
            cell: ({ row }) => {
              const hr = row.original;
              if (!canSeeValues(hr.project_id)) return <HintDash hint={NO_VALUES_HINT} />;
              if (hr.hour_tracking_mode === FIXED_MODE) return <HintDash hint={FIXED_MODE_HINT} />;
              if (hr.record_type === "ABSENCE") return <HintDash hint={ABSENCE_HINT} />;
              return formatBRL(hr.total_hours * (hr.hourly_rate || 0));
            },
          },
        ]
      : []),
    {
      id: "description",
      header: "Descrição",
      size: 300,
      maxSize: 380,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.description || "—")} $variant="paragraph_small_medium" $color="N900" />
          {row.original.status === "REJECTED" && row.original.rejection_reason ? (
            <Text
              $text="Motivo: %s"
              $replaces={[escapeHtml(row.original.rejection_reason)]}
              $variant="caption_medium"
              $color="N500"
            />
          ) : null}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      size: 110,
      cell: ({ row }) => <DomainStatus domain="horas" value={HOUR_STATUS[row.original.status] || row.original.status} />,
    },
    {
      id: "actions",
      size: 200,
      minSize: 200,
      maxSize: 200,
      align: "right",
      cell: ({ row }) =>
        row.original.status === "WAITING" && canDecide(row.original.project_id) ? (
          <Actions>
            <Button
              $text="Aprovar"
              $variant="outline"
              $size="small"
              $noWidth
              $isLoading={loadingDetailAction === row.original.id && !rejectTarget}
              $isDisabled={loadingDetailAction !== null || loadingAction}
              $action={() => approveHour(row.original.id)}
            />
            <Button
              $text="Reprovar"
              $variant="outline_danger"
              $size="small"
              $noWidth
              $isDisabled={loadingDetailAction !== null || loadingAction}
              $action={() => openRejectHour(row.original)}
            />
          </Actions>
        ) : null,
    },
  ];

  const noResults = filterStatus.value ? (
    <NoResults
      notResultText="Nenhum prestador com este status no período"
      includeText="Limpar filtros"
      includeAction={() => setFilterStatus(STATUS_DATA[0])}
    />
  ) : (
    <NoResults notResultText="Nenhum prestador com horas registradas para este período" />
  );

  const selectionStats = [{ label: "Horas", value: hrs(selectedSummary.hours) }];
  if (seesAnyValues) selectionStats.push({ label: "Valor", value: formatBRL(selectedSummary.value) });

  const approveSummary = approveBatch?.summary;

  return (
    <ContentPage>
      <PageActions>
        <HelpVideoButton videoKey="hoursApproval" />
      </PageActions>

      <ContentBox>
        <Stack $gap="24px">
          {/* Filtros */}
          <Filters>
            <SelectorPageStatus options={STATUS_DATA} selected={filterStatus} onChange={setFilterStatus} />
            <CompetenceField>
              <DatePicker
                $idForm="hours-approval-competence"
                $label="Competência"
                $variant="default"
                $isCompetence
                $popperPlacement="bottom-end"
                $date={new Date(competence.year, competence.month - 1, 1)}
                $setDate={(date) => {
                  if (!date) return;
                  setCompetence({ month: date.getMonth() + 1, year: date.getFullYear() });
                  setAutoFilterApplied(false);
                }}
              />
            </CompetenceField>
          </Filters>

          {/* Totais */}
          {loading ? <Skeleton $height={56} /> : <Totalizers totals={totals} />}
          {!loading && seesAnyValues && valueNotes.length ? (
            <Notice $variant="gray" $text={`O valor aguardando ${valueNotes.join(" e ")}.`} />
          ) : null}

          {/* Tabela */}
          <Stack>
            <BasicTable
              data={filteredContractors}
              columns={columns}
              getRowId={(row) => String(row.user_id)}
              isLoading={loading}
              noResults={noResults}
              enableRowSelection={(row) => actionableHourIds(row.original).length > 0}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              onRowClick={(contractor) => setDetailsUserId(contractor.user_id)}
            />
            <SelectionBar $count={selectedSummary.count} $stats={selectionStats} $onClear={() => setRowSelection({})}>
              <Button
                $text="Reprovar"
                $variant="outline_danger"
                $noWidth
                $isDisabled={loadingAction}
                $action={() => openRejectContractors(selectedContractors)}
              />
              <Button $text="Aprovar" $variant="primary" $noWidth $isDisabled={loadingAction} $action={openApproveBatch} />
            </SelectionBar>
          </Stack>
        </Stack>
      </ContentBox>

      {/* Modal: Detalhes do prestador */}
      {detailsContractor && (
        <ModalDefault
          $showModal={!!detailsContractor}
          $setShowModal={() => setDetailsUserId(null)}
          // Com a reprovação aberta por cima, o Esc e o clique fora fecham só ela
          $noClose={!!rejectTarget || loadingDetailAction !== null}
          $size="xlarge"
          $title={escapeHtml(`Horas de ${detailsContractor.user?.name || "prestador"} · ${formatCompetencia(competence)}`)}
          $contentBackground="N0">
          <Stack $gap="24px">
            <Totalizers
              totals={[
                { name: "Horas aguardando", value: hrs(detailsContractor.total_hours_waiting), color: "N900" },
                ...(seesAnyValues
                  ? [
                      {
                        name: "Valor aguardando",
                        value:
                          noVisibleValueWaiting(detailsContractor) ||
                          (onlyFixedWaiting(detailsContractor) && hiddenValueProjectsWaiting(detailsContractor).length === 0)
                            ? "—"
                            : formatBRL(detailsContractor.total_value_waiting || 0),
                        color: "N900",
                      },
                    ]
                  : []),
                { name: "Horas aprovadas", value: hrs(detailsContractor.total_hours_approved), color: "N900" },
              ]}
            />
            {seesAnyValues && hasWaitingHours(detailsContractor) ? (
              noVisibleValueWaiting(detailsContractor) ? (
                <Notice $variant="gray" $text={NO_VALUES_HINT} />
              ) : onlyFixedWaiting(detailsContractor) && hiddenValueProjectsWaiting(detailsContractor).length === 0 ? (
                <Notice $variant="gray" $text={FIXED_MODE_HINT} />
              ) : fixedProjectsWaiting(detailsContractor).length || hiddenValueProjectsWaiting(detailsContractor).length ? (
                <Notice
                  $variant="gray"
                  $text={`O valor aguardando ${[
                    fixedProjectsWaiting(detailsContractor).length ? "não inclui contrato de valor fixo" : null,
                    hiddenValueProjectsWaiting(detailsContractor).length ? NO_VALUES_NOTE : null,
                  ]
                    .filter(Boolean)
                    .join(" e ")}.`}
                />
              ) : null
            ) : null}
            <BasicTable
              data={detailsContractor.hour_records || []}
              // Sem lançamento a decidir, a coluna de ações sai e a descrição ganha o espaço
              columns={
                actionableHours(detailsContractor).length ? detailColumns : detailColumns.filter((c) => c.id !== "actions")
              }
              getRowId={(row) => String(row.id)}
              noResults={<NoResults notResultText="Nenhum lançamento neste período" />}
            />
          </Stack>
        </ModalDefault>
      )}

      {/* Modal: Confirmar aprovação em lote */}
      {approveBatch && (
        <ModalDefault
          $showModal={!!approveBatch}
          $setShowModal={() => !loadingAction && setApproveBatch(null)}
          $noClose={loadingAction}
          $size="medium"
          $title={`Aprovar horas de ${plural(approveSummary.count, "prestador", "prestadores")}`}
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $noWidth $isDisabled={loadingAction} $action={() => setApproveBatch(null)} />
              <Button $text="Aprovar" $variant="primary" $noWidth $isLoading={loadingAction} $action={confirmApproveBatch} />
            </>
          }>
          <Stack>
            <Text
              $text={
                seesAnyValues && approveSummary.value > 0
                  ? "<strong>%s</strong> de <strong>%s</strong> em %s, no valor de <strong>%s</strong>."
                  : "<strong>%s</strong> de <strong>%s</strong> em %s."
              }
              $replaces={[
                hrs(approveSummary.hours),
                plural(approveSummary.count, "prestador", "prestadores"),
                competenceLabel,
                formatBRL(approveSummary.value),
              ]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            {approveSummary.fixedCount > 0 ? (
              <Notice
                $variant="gray"
                $text={
                  approveSummary.value > 0
                    ? `Não inclui ${plural(approveSummary.fixedCount, "prestador", "prestadores")} com contrato de valor fixo: nesses, as horas ajustam o pagamento do mês, não o definem.`
                    : "Os prestadores selecionados têm contrato de valor fixo: as horas ajustam o pagamento do mês, não o definem."
                }
              />
            ) : null}
            {seesAnyValues && approveSummary.value > 0 && approveSummary.hiddenCount > 0 ? (
              <Notice $variant="gray" $text={`O valor ${NO_VALUES_NOTE}.`} />
            ) : null}
            <Text
              $text="Depois da aprovação, os cronogramas de pagamento são recalculados."
              $variant="caption_medium"
              $color="N500"
            />
          </Stack>
        </ModalDefault>
      )}

      {/* Modal: Reprovar com motivo */}
      {rejectTarget && (
        <RejectHoursModal
          title={escapeHtml(rejectTarget.title)}
          summary={rejectTarget.summary}
          loading={loadingAction}
          onConfirm={confirmReject}
          onClose={() => setRejectTarget(null)}
        />
      )}
    </ContentPage>
  );
}
